import { Link } from "wouter";
import {
  useGetSquad,
  useGetSquadSummary,
  useListMembers,
  useListSchedules,
  useCreateScheduleEntry,
  useUpdateScheduleEntry,
  useDeleteScheduleEntry,
  useCreateMember,
  useTransferCaptain,
  useCreateTransferRequest,
  useAcceptTransferRequest,
  useDismissTransferRequest,
  useUpdateSquad,
  useRegenerateInviteCode,
  getGetSquadSummaryQueryKey,
  getListMembersQueryKey,
  getGetCurrentUserQueryKey,
  getListSchedulesQueryKey,
} from "@workspace/api-client-react";
import type { CreateScheduleEntryBodyDayOfWeek } from "@workspace/api-client-react";
import type { Member, CaptainTransferRequest, ScheduleEntry } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useCurrentMember } from "@/hooks/useCurrentMember";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const SESSION_TYPES = [
  "Easy Run", "Long Run", "Tempo", "Interval", "Fartlek",
  "Recovery", "Cross-Train", "Rest", "Race", "Strength", "Other",
];


function SquadSessionModal({
  memberId,
  squadId,
  entry,
  onClose,
}: {
  memberId: number;
  squadId: number;
  entry?: ScheduleEntry;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const createEntry = useCreateScheduleEntry();
  const updateEntry = useUpdateScheduleEntry();

  const [weekNumber, setWeekNumber] = useState(String(entry?.weekNumber ?? 1));
  const [dayOfWeek, setDayOfWeek] = useState(entry?.dayOfWeek ?? "Monday");
  const [sessionType, setSessionType] = useState(entry?.sessionType ?? "Easy Run");
  const [targetMiles, setTargetMiles] = useState(entry?.targetMiles != null ? String(entry.targetMiles) : "");
  const [description, setDescription] = useState(entry?.description ?? "");
  const [error, setError] = useState("");

  const isEdit = !!entry;

  const invalidateSchedules = () => {
    queryClient.invalidateQueries({ queryKey: getListSchedulesQueryKey({ memberId }) });
    queryClient.invalidateQueries({ queryKey: getListSchedulesQueryKey({ squadId }) });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (isEdit) {
      updateEntry.mutate(
        {
          id: entry.id,
          data: {
            weekNumber: Number(weekNumber),
            dayOfWeek: dayOfWeek as CreateScheduleEntryBodyDayOfWeek,
            sessionType,
            targetMiles: targetMiles ? Number(targetMiles) : null,
            description: description.trim() || null,
          },
        },
        {
          onSuccess: () => { invalidateSchedules(); onClose(); },
          onError: () => setError("Failed to save session."),
        }
      );
    } else {
      createEntry.mutate(
        {
          data: {
            squadId,
            memberId,
            weekNumber: Number(weekNumber),
            dayOfWeek: dayOfWeek as CreateScheduleEntryBodyDayOfWeek,
            sessionType,
            targetMiles: targetMiles ? Number(targetMiles) : null,
            description: description.trim() || null,
          },
        },
        {
          onSuccess: () => { invalidateSchedules(); onClose(); },
          onError: () => setError("Failed to create session."),
        }
      );
    }
  };

  const isPending = createEntry.isPending || updateEntry.isPending;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="pr-modal"
      >
        <h2 className="font-display text-xl font-bold mb-6">
          {isEdit ? "Edit Session" : "Add Session"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="pr-label">Week #</label>
              <input
                type="number"
                value={weekNumber}
                onChange={(e) => setWeekNumber(e.target.value)}
                min={1}
                className="pr-field"
              />
            </div>
            <div>
              <label className="pr-label">Day</label>
              <select
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(e.target.value)}
                className="pr-field"
              >
                {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="pr-label">Session Type</label>
            <select
              value={sessionType}
              onChange={(e) => setSessionType(e.target.value)}
              className="pr-field"
            >
              {SESSION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="pr-label">Target Miles</label>
            <input
              type="number"
              value={targetMiles}
              onChange={(e) => setTargetMiles(e.target.value)}
              placeholder="e.g. 8"
              min={0}
              step={0.5}
              className="pr-field"
            />
          </div>
          <div>
            <label className="pr-label">Notes</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional notes, pace targets, etc."
              rows={2}
              className="pr-field"
            />
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={!weekNumber || isPending} className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              {isPending ? "Saving..." : isEdit ? "Save Changes" : "Add Session"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function CopySessionModal({
  entry,
  memberId,
  squadId,
  existingWeeks,
  onClose,
  onSuccess,
}: {
  entry: ScheduleEntry;
  memberId: number;
  squadId: number;
  existingWeeks: number[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const createEntry = useCreateScheduleEntry();
  const [targetWeek, setTargetWeek] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const week = Number(targetWeek);
    if (!week || week < 1) {
      setError("Enter a valid week number.");
      return;
    }
    setError("");
    createEntry.mutate(
      {
        data: {
          squadId,
          memberId,
          weekNumber: week,
          dayOfWeek: entry.dayOfWeek as CreateScheduleEntryBodyDayOfWeek,
          sessionType: entry.sessionType,
          targetMiles: entry.targetMiles ?? null,
          description: entry.description ?? null,
        },
      },
      {
        onSuccess: () => { onSuccess(); onClose(); },
        onError: () => setError("Failed to copy session. Please try again."),
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="pr-modal"
      >
        <h2 className="font-display text-xl font-bold mb-2">Copy Session</h2>
        <p className="text-sm text-muted-foreground mb-5">
          Copying{" "}
          <span className="font-semibold text-foreground">{entry.sessionType}</span>{" "}
          on{" "}
          <span className="font-semibold text-foreground">{entry.dayOfWeek}</span>
          {entry.targetMiles ? ` · ${entry.targetMiles}mi` : ""} to another week.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="pr-label">
              Target Week
            </label>
            <input
              type="number"
              value={targetWeek}
              onChange={(e) => setTargetWeek(e.target.value)}
              placeholder="e.g. 3"
              min={1}
              autoFocus
              className="pr-field"
            />
            {existingWeeks.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {existingWeeks.map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => setTargetWeek(String(w))}
                    className={cn(
                      "px-2 py-0.5 rounded text-xs font-mono font-bold transition-colors border",
                      String(w) === targetWeek
                        ? "bg-foreground text-background border-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    )}
                  >
                    Wk {w}
                  </button>
                ))}
              </div>
            )}
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!targetWeek || createEntry.isPending}
              className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {createEntry.isPending ? "Copying..." : "Copy Session"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function CopyWeekModal({
  entries,
  memberId,
  squadId,
  sourceWeek,
  existingWeeks,
  onClose,
  onSuccess,
}: {
  entries: ScheduleEntry[];
  memberId: number;
  squadId: number;
  sourceWeek: number;
  existingWeeks: number[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const createEntry = useCreateScheduleEntry();
  const [targetWeek, setTargetWeek] = useState("");
  const [error, setError] = useState("");
  const [isPending, setIsPending] = useState(false);

  const activeSessions = entries.filter(
    (e) => e.sessionType.toLowerCase() !== "rest"
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const week = Number(targetWeek);
    if (!week || week < 1) {
      setError("Enter a valid week number.");
      return;
    }
    setError("");
    setIsPending(true);

    let completed = 0;
    let failed = false;

    const finish = () => {
      completed += 1;
      if (completed === activeSessions.length) {
        setIsPending(false);
        if (!failed) {
          onSuccess();
          onClose();
        }
      }
    };

    activeSessions.forEach((entry) => {
      createEntry.mutate(
        {
          data: {
            squadId,
            memberId,
            weekNumber: week,
            dayOfWeek: entry.dayOfWeek as CreateScheduleEntryBodyDayOfWeek,
            sessionType: entry.sessionType,
            targetMiles: entry.targetMiles ?? null,
            description: entry.description ?? null,
          },
        },
        {
          onSuccess: finish,
          onError: () => {
            failed = true;
            setError("One or more sessions failed to copy. Please try again.");
            finish();
          },
        }
      );
    });
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="pr-modal"
      >
        <h2 className="font-display text-xl font-bold mb-1">Copy Week {sourceWeek}</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {activeSessions.length === 0
            ? "No sessions to copy in this week."
            : `Copying ${activeSessions.length} session${activeSessions.length === 1 ? "" : "s"} to another week.`}
        </p>
        {activeSessions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-5">
            {activeSessions.map((entry) => (
              <span
                key={entry.id}
                className="px-2 py-0.5 rounded text-xs font-mono border border-border text-muted-foreground"
              >
                {entry.dayOfWeek.slice(0, 3)} · {entry.sessionType}
                {entry.targetMiles ? ` ${entry.targetMiles}mi` : ""}
              </span>
            ))}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="pr-label">
              Target Week
            </label>
            <input
              type="number"
              value={targetWeek}
              onChange={(e) => setTargetWeek(e.target.value)}
              placeholder="e.g. 3"
              min={1}
              autoFocus
              disabled={activeSessions.length === 0}
              className="pr-field"
            />
            {existingWeeks.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {existingWeeks.map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => setTargetWeek(String(w))}
                    className={cn(
                      "px-2 py-0.5 rounded text-xs font-mono font-bold transition-colors border",
                      String(w) === targetWeek
                        ? "bg-foreground text-background border-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    )}
                  >
                    Wk {w}
                  </button>
                ))}
              </div>
            )}
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!targetWeek || isPending || activeSessions.length === 0}
              className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {isPending ? "Copying..." : "Copy Week"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function CoachingScheduleView({
  squadId,
  members,
  readOnly = false,
  currentMemberId,
}: {
  squadId: number;
  members: Member[];
  readOnly?: boolean;
  currentMemberId?: number;
}) {
  const queryClient = useQueryClient();
  const deleteEntry = useDeleteScheduleEntry();

  const scheduleQueryKey = getListSchedulesQueryKey({ squadId });

  const { data: schedules, isLoading } = useListSchedules(
    { squadId },
    { query: { queryKey: scheduleQueryKey } }
  );

  const allWeeks = Array.from(
    new Set((schedules ?? []).map((s) => s.weekNumber))
  ).sort((a, b) => a - b);

  const [selectedWeek, setSelectedWeek] = useState<number | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);
  const [modalMemberId, setModalMemberId] = useState<number | undefined>(undefined);
  const [editingEntry, setEditingEntry] = useState<ScheduleEntry | undefined>(undefined);
  const [copyingEntry, setCopyingEntry] = useState<ScheduleEntry | undefined>(undefined);
  const [copyingMemberId, setCopyingMemberId] = useState<number | undefined>(undefined);
  const [copyWeekMemberId, setCopyWeekMemberId] = useState<number | undefined>(undefined);
  const [deleteError, setDeleteError] = useState("");
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<number | undefined>(undefined);

  const activeWeek = selectedWeek ?? allWeeks[0];

  const weekEntries = (schedules ?? []).filter((s) => s.weekNumber === activeWeek);

  const handleDelete = (entry: ScheduleEntry) => {
    setDeleteError("");
    setConfirmingDeleteId(undefined);
    deleteEntry.mutate(
      { id: entry.id },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: scheduleQueryKey }),
        onError: () => setDeleteError("Failed to delete session."),
      }
    );
  };

  const openAddModal = (memberId: number) => {
    setModalMemberId(memberId);
    setEditingEntry(undefined);
    setShowModal(true);
  };

  const openEditModal = (entry: ScheduleEntry) => {
    setModalMemberId(entry.memberId ?? undefined);
    setEditingEntry(entry);
    setShowModal(true);
  };

  if (members.length === 0) return null;

  return (
    <div className="border border-border rounded">
      {showModal && modalMemberId && (!readOnly || modalMemberId === currentMemberId) && (
        <SquadSessionModal
          memberId={modalMemberId}
          squadId={squadId}
          entry={editingEntry}
          onClose={() => { setShowModal(false); setEditingEntry(undefined); setModalMemberId(undefined); }}
        />
      )}
      {copyingEntry && copyingMemberId != null && (
        <CopySessionModal
          entry={copyingEntry}
          memberId={copyingMemberId}
          squadId={squadId}
          existingWeeks={allWeeks}
          onClose={() => { setCopyingEntry(undefined); setCopyingMemberId(undefined); }}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: scheduleQueryKey })}
        />
      )}
      {copyWeekMemberId != null && activeWeek != null && (
        <CopyWeekModal
          entries={weekEntries.filter((e) => e.memberId === copyWeekMemberId)}
          memberId={copyWeekMemberId}
          squadId={squadId}
          sourceWeek={activeWeek}
          existingWeeks={allWeeks}
          onClose={() => setCopyWeekMemberId(undefined)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: scheduleQueryKey })}
        />
      )}

      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
            Training Schedule
          </h2>
          {readOnly && (
            <span className="text-xs font-mono text-muted-foreground/60 border border-border rounded px-1.5 py-0.5">
              view only
            </span>
          )}
        </div>
        {allWeeks.length > 0 && (
          <div className="flex items-center gap-1.5">
            {allWeeks.map((w) => (
              <button
                key={w}
                onClick={() => setSelectedWeek(w)}
                className={cn(
                  "px-2.5 py-1 rounded text-xs font-mono font-bold transition-colors",
                  activeWeek === w
                    ? "bg-foreground text-background"
                    : "border border-border text-muted-foreground hover:text-foreground"
                )}
              >
                Wk {w}
              </button>
            ))}
          </div>
        )}
      </div>

      {deleteError && (
        <div className="px-5 py-2 border-b border-border">
          <p className="text-destructive text-sm">{deleteError}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <span className="text-muted-foreground font-mono text-sm animate-pulse">Loading schedule...</span>
        </div>
      ) : allWeeks.length === 0 ? (
        <div className="px-5 py-8">
          {readOnly && currentMemberId == null ? (
            <p className="text-muted-foreground text-sm text-center">No sessions planned yet.</p>
          ) : (
            <>
              <p className="text-muted-foreground text-sm text-center mb-4">
                {readOnly
                  ? "No sessions planned yet. Add your first session:"
                  : "No sessions planned yet. Add the first session for any runner:"}
              </p>
              <div className="flex flex-col border border-border rounded">
                {members
                  .filter((member) => !readOnly || member.id === currentMemberId)
                  .map((member) => (
                    // WI#21 — the row is a plain list row; only "+ Add Session" is clickable, styled
                    // like "+ Add Runner" on the Runners card.
                    <div
                      key={member.id}
                      className="flex items-center justify-between px-4 py-2.5 border-b border-border last:border-b-0"
                    >
                      <span className="text-sm font-medium text-foreground">{member.name}</span>
                      <button
                        onClick={() => openAddModal(member.id)}
                        className="text-xs font-semibold text-foreground underline underline-offset-2 hover:text-muted-foreground"
                      >
                        + Add Session
                      </button>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[680px]">
            <div className="grid grid-cols-[140px_repeat(7,1fr)] border-b border-border bg-zinc-100">
              <div className="px-3 py-2.5 border-r border-border">
                <span className="text-xs font-mono font-semibold text-foreground uppercase tracking-wider">Runner</span>
              </div>
              {DAYS.map((day) => (
                <div key={day} className="px-2 py-2.5 text-center border-r border-border last:border-r-0">
                  <span className="text-xs font-mono font-semibold text-foreground uppercase">
                    {day.slice(0, 3)}
                  </span>
                </div>
              ))}
            </div>

            {members.map((member) => {
              const memberEntries = weekEntries.filter((e) => e.memberId === member.id);
              return (
                <div key={member.id} className="grid grid-cols-[140px_repeat(7,1fr)] border-b border-border last:border-b-0">
                  <div className="px-3 py-3 border-r border-border flex flex-col justify-between">
                    <div>
                      <div className="text-xs font-semibold text-foreground leading-tight">{member.name}</div>
                      {member.role === "captain" && (
                        <div className="text-xs font-mono text-primary mt-0.5">captain</div>
                      )}
                    </div>
                    {(!readOnly || member.id === currentMemberId) && (
                      <div className="flex flex-col gap-0.5 mt-1.5">
                        <button
                          onClick={() => openAddModal(member.id)}
                          className="text-xs text-muted-foreground hover:text-foreground font-mono text-left"
                        >
                          + add
                        </button>
                        {memberEntries.some((e) => e.sessionType.toLowerCase() !== "rest") && (
                          <button
                            onClick={() => setCopyWeekMemberId(member.id)}
                            className="text-xs text-muted-foreground hover:text-primary font-mono text-left"
                          >
                            copy week
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {DAYS.map((day) => {
                    const entry = memberEntries.find((e) => e.dayOfWeek === day);
                    const isRest = !entry || entry.sessionType.toLowerCase() === "rest";
                    return (
                      <div key={day} className="border-r border-border last:border-r-0 p-2 min-h-[90px] group">
                        {!isRest && entry ? (
                          <div>
                            <div className="text-xs font-semibold text-foreground leading-tight mb-1">
                              {entry.sessionType}
                            </div>
                            {entry.targetMiles && (
                              <span className="inline-block bg-primary text-primary-foreground text-xs font-mono font-bold px-1.5 py-0.5 rounded-sm mb-1">
                                {entry.targetMiles}mi
                              </span>
                            )}
                            {entry.description && (
                              <p className="text-xs text-muted-foreground leading-tight">{entry.description}</p>
                            )}
                            {(!readOnly || member.id === currentMemberId) && (
                              <div className="flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => openEditModal(entry)}
                                  className="text-xs text-muted-foreground hover:text-foreground font-mono"
                                >
                                  edit
                                </button>
                                <span className="text-muted-foreground text-xs">·</span>
                                <button
                                  onClick={() => { setCopyingEntry(entry); setCopyingMemberId(member.id); }}
                                  className="text-xs text-muted-foreground hover:text-foreground font-mono"
                                >
                                  copy
                                </button>
                                <span className="text-muted-foreground text-xs">·</span>
                                {confirmingDeleteId === entry.id ? (
                                  <>
                                    <span className="text-xs text-destructive font-mono">Delete?</span>
                                    <button
                                      onClick={() => handleDelete(entry)}
                                      disabled={deleteEntry.isPending}
                                      className="text-xs text-destructive font-mono font-semibold hover:text-destructive disabled:opacity-50"
                                    >
                                      yes
                                    </button>
                                    <span className="text-muted-foreground text-xs">·</span>
                                    <button
                                      onClick={() => setConfirmingDeleteId(undefined)}
                                      className="text-xs text-muted-foreground hover:text-foreground font-mono"
                                    >
                                      cancel
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => setConfirmingDeleteId(entry.id)}
                                    disabled={deleteEntry.isPending}
                                    className="text-xs text-destructive/70 hover:text-destructive font-mono disabled:opacity-50"
                                  >
                                    del
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground/40">—</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SquadSettingsModal({
  squadId,
  squad,
  onClose,
}: {
  squadId: number;
  squad: { name: string; description?: string | null; goalRace?: string | null; weeklyMileageGoal?: number | null; inviteCode: string; welcomeMessage?: string | null };
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const updateSquad = useUpdateSquad();
  const regenerateInvite = useRegenerateInviteCode();

  const [name, setName] = useState(squad.name);
  const [description, setDescription] = useState(squad.description ?? "");
  const [goalRace, setGoalRace] = useState(squad.goalRace ?? "");
  const [weeklyMileageGoal, setWeeklyMileageGoal] = useState(
    squad.weeklyMileageGoal != null ? String(squad.weeklyMileageGoal) : ""
  );
  const [welcomeMessage, setWelcomeMessage] = useState(squad.welcomeMessage ?? "");
  const [inviteCode, setInviteCode] = useState(squad.inviteCode);
  const [saveError, setSaveError] = useState("");
  const [regenError, setRegenError] = useState("");
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
  const inviteLink = `${window.location.origin}${basePath}/join?code=${inviteCode}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const squadQueryKey = [["squads", squadId]];

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaveError("");
    updateSquad.mutate(
      {
        id: squadId,
        data: {
          name: name.trim(),
          description: description.trim() || null,
          goalRace: goalRace.trim() || null,
          weeklyMileageGoal: weeklyMileageGoal ? Number(weeklyMileageGoal) : null,
          welcomeMessage: welcomeMessage.trim() || null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: squadQueryKey });
          queryClient.invalidateQueries({ queryKey: getGetSquadSummaryQueryKey(squadId) });
          onClose();
        },
        onError: () => setSaveError("Failed to save settings. Please try again."),
      }
    );
  };

  const handleRegenerate = () => {
    setRegenError("");
    regenerateInvite.mutate(
      { id: squadId },
      {
        onSuccess: (data) => {
          setInviteCode(data.inviteCode);
          queryClient.invalidateQueries({ queryKey: squadQueryKey });
        },
        onError: () => setRegenError("Failed to regenerate invite code. Please try again."),
      }
    );
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(inviteCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="pr-modal"
      >
        <h2 className="font-display text-xl font-bold mb-6">Squad Settings</h2>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="pr-label">
              Squad Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Squad name"
              className="pr-field"
              autoFocus
            />
          </div>
          <div>
            <label className="pr-label">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this squad about? Training focus, vibe, goals…"
              rows={3}
              className="pr-field"
            />
          </div>
          <div>
            <label className="pr-label">
              Goal Race
            </label>
            <input
              type="text"
              value={goalRace}
              onChange={(e) => setGoalRace(e.target.value)}
              placeholder="Boston 2025"
              className="pr-field"
            />
          </div>
          <div>
            <label className="pr-label">
              Weekly Miles Goal
            </label>
            <input
              type="number"
              value={weeklyMileageGoal}
              onChange={(e) => setWeeklyMileageGoal(e.target.value)}
              placeholder="40"
              min={0}
              className="pr-field"
            />
          </div>

          <div>
            <label className="pr-label">
              Welcome Message
            </label>
            <textarea
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              placeholder="Hey, welcome to the squad! We train hard Mon/Wed/Fri and keep each other honest. Check in every day — no excuses."
              rows={3}
              className="pr-field"
            />
            <p className="text-muted-foreground text-xs mt-1">
              Shown once to new runners when they join via invite code.
            </p>
          </div>

          <div>
            <label className="pr-label">
              Invite Code
            </label>
            <div className="flex gap-2">
              <div className="flex-1 h-10 flex items-center bg-background border border-foreground/20 rounded px-3 text-sm font-mono text-foreground tracking-widest">
                {inviteCode}
              </div>
              <button
                type="button"
                onClick={handleCopyCode}
                className="h-10 border border-border rounded px-3 text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <div className="mt-2">
              <button
                type="button"
                onClick={handleRegenerate}
                disabled={regenerateInvite.isPending}
                className="text-xs text-destructive hover:text-destructive/80 font-semibold disabled:opacity-50 transition-colors"
              >
                {regenerateInvite.isPending ? "Regenerating..." : "Regenerate invite code"}
              </button>
              {regenError && <p className="text-destructive text-xs mt-1">{regenError}</p>}
              <p className="text-muted-foreground text-xs mt-1">
                The old code will stop working immediately.
              </p>
            </div>
            <div className="mt-3">
              <label className="pr-label">
                Shareable Link
              </label>
              <div className="flex gap-2">
                <div className="flex-1 min-w-0 h-10 flex items-center bg-background border border-foreground/20 rounded px-3 text-xs font-mono text-muted-foreground select-all">
                  <span className="truncate">{inviteLink}</span>
                </div>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="h-10 border border-border rounded px-3 text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                >
                  {linkCopied ? "Copied!" : "Copy Link"}
                </button>
              </div>
              <p className="text-muted-foreground text-xs mt-1">
                Anyone with this link can join the squad directly.
              </p>
            </div>
          </div>

          {saveError && <p className="text-destructive text-sm">{saveError}</p>}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || updateSquad.isPending}
              className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {updateSquad.isPending ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function AddMemberModal({ squadId, onClose }: { squadId: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const createMember = useCreateMember();
  const [name, setName] = useState("");
  const [goalRace, setGoalRace] = useState("");
  const [weeklyMileageGoal, setWeeklyMileageGoal] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createMember.mutate(
      {
        data: {
          squadId,
          name: name.trim(),
          goalRace: goalRace.trim() || null,
          weeklyMileageGoal: weeklyMileageGoal ? Number(weeklyMileageGoal) : null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListMembersQueryKey({ squadId }) });
          queryClient.invalidateQueries({ queryKey: getGetSquadSummaryQueryKey(squadId) });
          onClose();
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="pr-modal"
      >
        <h2 className="font-display text-xl font-bold mb-6">Add Runner</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="pr-label">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Runner name"
              className="pr-field"
              autoFocus
            />
          </div>
          <div>
            <label className="pr-label">Goal Race</label>
            <input
              type="text"
              value={goalRace}
              onChange={(e) => setGoalRace(e.target.value)}
              placeholder="Boston 2025"
              className="pr-field"
            />
          </div>
          <div>
            <label className="pr-label">Weekly Miles</label>
            <input
              type="number"
              value={weeklyMileageGoal}
              onChange={(e) => setWeeklyMileageGoal(e.target.value)}
              placeholder="40"
              min={0}
              className="pr-field"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || createMember.isPending}
              className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {createMember.isPending ? "Adding..." : "Add Runner"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function TransferCaptainModal({
  squadId,
  members,
  currentMemberId,
  onClose,
}: {
  squadId: number;
  members: Member[];
  currentMemberId: number | undefined;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const transferCaptain = useTransferCaptain();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [error, setError] = useState("");

  const eligibleRunners = members.filter(
    (m) => m.id !== currentMemberId && m.role !== "captain"
  );

  const handleTransfer = () => {
    if (!selectedId) return;
    setError("");
    transferCaptain.mutate(
      { id: squadId, data: { newCaptainMemberId: selectedId } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListMembersQueryKey({ squadId }) });
          queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
          onClose();
        },
        onError: () => {
          setError("Failed to transfer captaincy. Please try again.");
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="pr-modal"
      >
        <h2 className="font-display text-xl font-bold mb-2">Transfer Captaincy</h2>
        <p className="text-muted-foreground text-sm mb-5">
          Choose a runner to promote to captain. You will become a regular runner.
        </p>
        {eligibleRunners.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-4">
            No other runners to transfer to.
          </p>
        ) : (
          <div className="space-y-2 mb-5 max-h-60 overflow-y-auto">
            {eligibleRunners.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelectedId(m.id)}
                className={cn(
                  "w-full text-left px-4 py-3 rounded border transition-colors",
                  selectedId === m.id
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/40"
                )}
              >
                <span className="font-medium text-sm">{m.name}</span>
                {m.goalRace && (
                  <span className="text-xs text-muted-foreground font-mono ml-2">{m.goalRace}</span>
                )}
              </button>
            ))}
          </div>
        )}
        {error && <p className="text-destructive text-sm mb-3">{error}</p>}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleTransfer}
            disabled={!selectedId || transferCaptain.isPending || eligibleRunners.length === 0}
            className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {transferCaptain.isPending ? "Transferring..." : "Transfer"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function CaptainTransferBanner({
  squadId,
  pendingTransferRequest,
}: {
  squadId: number;
  pendingTransferRequest: CaptainTransferRequest | null | undefined;
}) {
  const queryClient = useQueryClient();
  const acceptTransfer = useAcceptTransferRequest();
  const dismissTransfer = useDismissTransferRequest();
  const [actionError, setActionError] = useState("");

  if (!pendingTransferRequest) return null;

  const expiresAt = new Date(pendingTransferRequest.expiresAt);
  const now = new Date();
  const hoursRemaining = Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60)));

  const handleAccept = () => {
    setActionError("");
    acceptTransfer.mutate(
      { id: squadId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetSquadSummaryQueryKey(squadId) });
          queryClient.invalidateQueries({ queryKey: getListMembersQueryKey({ squadId }) });
          queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
        },
        onError: () => setActionError("Failed to accept transfer. Please try again."),
      }
    );
  };

  const handleDismiss = () => {
    setActionError("");
    dismissTransfer.mutate(
      { id: squadId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetSquadSummaryQueryKey(squadId) });
        },
        onError: () => setActionError("Failed to dismiss request. Please try again."),
      }
    );
  };

  return (
    <div className="border border-destructive/40 bg-destructive/5 rounded p-4 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground mb-0.5">
            Captain transfer requested
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium">{pendingTransferRequest.requestedByMemberName}</span> has requested a captain transfer.
            {" "}Accept to transfer captaincy to the longest-tenured runner, or dismiss to cancel.
            {" "}You have <span className="font-semibold text-foreground">{hoursRemaining}h</span> remaining.
          </p>
          {actionError && <p className="text-destructive text-xs mt-1">{actionError}</p>}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={handleDismiss}
            disabled={dismissTransfer.isPending || acceptTransfer.isPending}
            className="border border-border rounded px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            {dismissTransfer.isPending ? "Dismissing..." : "Dismiss"}
          </button>
          <button
            onClick={handleAccept}
            disabled={acceptTransfer.isPending || dismissTransfer.isPending}
            className="bg-destructive text-destructive-foreground rounded px-3 py-1.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {acceptTransfer.isPending ? "Transferring..." : "Accept Transfer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RunnerTransferSection({
  squadId,
  captainIsInactive,
  pendingTransferRequest,
  captainName,
}: {
  squadId: number;
  captainIsInactive: boolean;
  pendingTransferRequest: CaptainTransferRequest | null | undefined;
  captainName: string | undefined;
}) {
  const queryClient = useQueryClient();
  const createTransferRequest = useCreateTransferRequest();
  const [requested, setRequested] = useState(false);
  const [error, setError] = useState("");

  if (pendingTransferRequest) {
    const expiresAt = new Date(pendingTransferRequest.expiresAt);
    const now = new Date();
    const hoursRemaining = Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60)));
    return (
      <div className="border border-primary/20 bg-primary/5 rounded p-4 mb-6 flex items-start gap-3">
        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
        <p className="text-sm text-foreground">
          <span className="font-semibold">Captain transfer requested.</span>{" "}
          If {captainName ?? "the captain"} doesn't respond in {hoursRemaining}h, captaincy transfers automatically.
        </p>
      </div>
    );
  }

  if (!captainIsInactive) return null;

  if (requested) {
    return (
      <div className="border border-primary/20 bg-primary/5 rounded p-4 mb-6 flex items-start gap-3">
        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
        <p className="text-sm text-foreground">
          <span className="font-semibold">Request sent.</span>{" "}
          If {captainName ?? "the captain"} doesn't respond in 48 hours, captaincy transfers automatically.
        </p>
      </div>
    );
  }

  const handleRequest = () => {
    setError("");
    createTransferRequest.mutate(
      { id: squadId },
      {
        onSuccess: () => {
          setRequested(true);
          queryClient.invalidateQueries({ queryKey: getGetSquadSummaryQueryKey(squadId) });
        },
        onError: () => setError("Failed to submit request. Please try again."),
      }
    );
  };

  return (
    <div className="border border-border rounded p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex-1">
        <p className="text-sm text-foreground">
          <span className="font-semibold">{captainName ?? "The captain"}</span> hasn't checked in for 14+ days.
          You can request a captain transfer.
        </p>
        {error && <p className="text-destructive text-xs mt-1">{error}</p>}
      </div>
      <button
        onClick={handleRequest}
        disabled={createTransferRequest.isPending}
        className="flex-shrink-0 border border-destructive text-destructive rounded px-3 py-1.5 text-sm font-semibold hover:bg-destructive/5 transition-colors disabled:opacity-50"
      >
        {createTransferRequest.isPending ? "Requesting..." : "Request Captain Transfer"}
      </button>
    </div>
  );
}

export default function SquadDetail({ squadId }: { squadId: number }) {
  const { data: squad, isLoading: squadLoading, isError: squadError } = useGetSquad(squadId, {
    query: { enabled: !!squadId, queryKey: [["squads", squadId]] },
  });
  const { data: summary } = useGetSquadSummary(squadId, {
    query: { enabled: !!squadId, queryKey: getGetSquadSummaryQueryKey(squadId) },
  });
  const { data: members } = useListMembers(
    { squadId },
    { query: { enabled: !!squadId, queryKey: getListMembersQueryKey({ squadId }) } }
  );
  const [showAddMember, setShowAddMember] = useState(false);
  const [showTransferCaptain, setShowTransferCaptain] = useState(false);
  const [showSquadSettings, setShowSquadSettings] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  const handleCopyInviteCode = () => {
    if (!squad?.inviteCode) return;
    navigator.clipboard.writeText(squad.inviteCode).then(() => {
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    }).catch(() => {});
  };

  const { canManageSquad, member: currentMember, isCaptain } = useCurrentMember(squadId);

  if (squadLoading) {
    return (
      <div className="animate-pulse">
        {/* Header skeleton */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="h-3 bg-muted rounded w-20 mb-2" />
            <div className="h-8 bg-muted rounded w-56 mb-2" />
            <div className="h-3 bg-muted rounded w-40" />
          </div>
          <div className="flex gap-3">
            <div className="h-9 bg-muted rounded w-28" />
            <div className="h-9 bg-muted rounded w-28" />
          </div>
        </div>

        {/* Stats grid skeleton */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-card p-5">
              <div className="h-8 bg-muted rounded w-16 mb-2" />
              <div className="h-3 bg-muted rounded w-20" />
            </div>
          ))}
        </div>

        {/* Member list skeleton */}
        <div className="border border-border rounded">
          <div className="px-5 py-4 border-b border-border">
            <div className="h-4 bg-muted rounded w-24" />
          </div>
          <div className="divide-y divide-border">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="px-5 py-4 flex items-center justify-between">
                <div>
                  <div className="h-4 bg-muted rounded w-32 mb-2" />
                  <div className="h-3 bg-muted rounded w-20" />
                </div>
                <div className="h-6 bg-muted rounded w-16" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (squadError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="text-destructive font-mono text-sm font-medium">Unable to load squad</div>
        <div className="text-muted-foreground text-xs text-center max-w-xs">
          There was a problem fetching your data. Check your connection or try refreshing the page.
        </div>
      </div>
    );
  }

  if (!squad) {
    return (
      <div className="text-center py-24">
        <p className="text-muted-foreground">Squad not found.</p>
        <Link href="/squads" className="text-muted-foreground text-sm mt-2 inline-block hover:text-foreground underline">
          Back to Squads
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link href="/squads" className="text-muted-foreground text-sm hover:text-foreground transition-colors">
              Squads
            </Link>
            <span className="text-muted-foreground text-sm">/</span>
          </div>
          <h1 className="font-display text-3xl font-bold">{squad.name}</h1>
          {squad.description && (
            <p className="text-muted-foreground text-sm mt-1">{squad.description}</p>
          )}
        </div>
        <div className="flex gap-3">
          {canManageSquad && (
            <button
              onClick={() => setShowSquadSettings(true)}
              className="border border-border text-foreground px-3 py-2 rounded text-sm hover:bg-accent/30 transition-colors"
            >
              Squad Settings
            </button>
          )}
          {isCaptain && (
            <button
              onClick={() => setShowTransferCaptain(true)}
              className="border border-border text-foreground px-3 py-2 rounded text-sm hover:bg-accent/30 transition-colors"
            >
              Transfer Captain
            </button>
          )}
          <Link
            href={`/squads/${squadId}/checkin`}
            className="bg-primary text-primary-foreground px-4 py-2 rounded text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Log Check-in
          </Link>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border mb-8">
          {[
            { label: "Members", value: summary.memberCount },
            { label: "Check-ins", value: summary.totalCheckins },
            { label: "Adherence", value: `${summary.adherencePct}%` },
            { label: "Top Streak", value: `${summary.topStreak}d` },
          ].map((stat) => (
            <div key={stat.label} className="bg-card p-5">
              <div className="font-display text-3xl font-bold text-foreground mb-0.5">{stat.value}</div>
              <div className="text-muted-foreground text-xs uppercase tracking-wider font-mono">{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {canManageSquad && squad.inviteCode && (
        <div className="border border-border rounded p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
              Invite Code
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-bold tracking-widest bg-input border border-border rounded px-3 py-1.5 text-foreground select-all">
                {squad.inviteCode}
              </span>
              <button
                onClick={handleCopyInviteCode}
                className="border border-border rounded px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              >
                {inviteCopied ? "Copied!" : "Copy"}
              </button>
            </div>
            <div className="text-xs text-muted-foreground mt-2 font-mono">
              {squad.inviteJoinCount === 1
                ? "1 runner joined with this code"
                : `${squad.inviteJoinCount} runners joined with this code`}
            </div>
          </div>
          <button
            onClick={() => setShowSquadSettings(true)}
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors flex-shrink-0"
          >
            Regenerate
          </button>
        </div>
      )}

      {isCaptain && summary?.pendingTransferRequest && (
        <CaptainTransferBanner
          squadId={squadId}
          pendingTransferRequest={summary.pendingTransferRequest}
        />
      )}

      {!isCaptain && summary && (
        <RunnerTransferSection
          squadId={squadId}
          captainIsInactive={summary.captainIsInactive}
          pendingTransferRequest={summary.pendingTransferRequest ?? null}
          captainName={members?.find((m) => m.role === "captain")?.name}
        />
      )}

      {summary && summary.quietMembers.length > 0 && (
        <div className="border border-primary/20 bg-primary/5 rounded p-4 mb-6 flex items-start gap-3">
          <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
          <p className="text-sm text-foreground">
            <span className="font-semibold">Quiet for 2+ days:</span>{" "}
            {summary.quietMembers.join(", ")}. Time to check in.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <div className="border border-border rounded">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                Runners
              </h2>
              {canManageSquad && (
                <button
                  onClick={() => setShowAddMember(true)}
                  className="text-xs font-semibold text-foreground underline underline-offset-2 hover:text-muted-foreground"
                >
                  + Add Runner
                </button>
              )}
            </div>
            <div className="divide-y divide-border">
              {members?.length === 0 ? (
                <div className="px-5 py-8 text-center text-muted-foreground text-sm">
                  No members yet.
                  {canManageSquad && (
                    <button onClick={() => setShowAddMember(true)} className="ml-1 underline hover:text-muted-foreground">
                      Add the first runner.
                    </button>
                  )}
                </div>
              ) : (
                members?.map((member, i) => (
                  <Link key={member.id} href={`/members/${member.id}`}>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.05 }}
                      className="px-5 py-4 flex items-center gap-4 hover:bg-accent/20 transition-colors cursor-pointer"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{member.name}</span>
                          {member.role === "captain" && (
                            <span className="text-xs font-mono bg-primary text-primary-foreground rounded px-1.5 py-0.5">
                              captain
                            </span>
                          )}
                          {canManageSquad && member.joinSource === "invite" && (
                            <span className="text-xs font-mono bg-secondary text-muted-foreground rounded px-1.5 py-0.5 border border-border">
                              via invite
                            </span>
                          )}
                        </div>
                        {member.goalRace && (
                          <div className="text-muted-foreground text-xs font-mono mt-0.5">{member.goalRace}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-right">
                        <div>
                          <div className="font-mono text-sm font-bold text-foreground">{member.currentStreak}d</div>
                          <div className="text-muted-foreground text-xs font-mono">streak</div>
                        </div>
                        <div className="w-20">
                          <div className="flex items-center justify-end gap-1.5 mb-1">
                            <span className="font-mono text-xs text-foreground">{member.adherencePct}%</span>
                          </div>
                          <div className="h-1 bg-secondary rounded-full overflow-hidden">
                            <div
                              className={cn(
                                "h-full rounded-full",
                                member.adherencePct >= 80 ? "bg-primary" : member.adherencePct >= 60 ? "bg-chart-2" : "bg-destructive/60"
                              )}
                              style={{ width: `${member.adherencePct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="border border-border rounded">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                Recent Activity
              </h2>
            </div>
            <div className="divide-y divide-border">
              {summary?.recentActivity.slice(0, 10).map((item, i) => {
                const isCaptainTransfer = item.status === "became_captain";
                return (
                  <div key={i} className="px-5 py-3 flex items-start gap-2">
                    <span
                      className={cn(
                        "w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
                        isCaptainTransfer ? "bg-chart-2" : item.status === "crushed" ? "bg-primary" : "bg-muted-foreground/40"
                      )}
                    />
                    <div className="min-w-0">
                      {isCaptainTransfer ? (
                        <div className="text-sm">
                          <span className="text-muted-foreground">{item.note ?? `${item.memberName} became squad captain.`}</span>
                        </div>
                      ) : (
                        <div className="text-sm">
                          <span className="font-medium">{item.memberName}</span>
                          <span className="text-muted-foreground">
                            {" "}{item.status === "crushed" ? "crushed it" : "skipped"}
                          </span>
                        </div>
                      )}
                      {!isCaptainTransfer && item.note && (
                        <div className="text-muted-foreground text-xs mt-0.5 italic">{item.note}</div>
                      )}
                      <div className="text-muted-foreground text-xs font-mono">{item.checkinDate}</div>
                    </div>
                  </div>
                );
              })}
              {(!summary || summary.recentActivity.length === 0) && (
                <div className="px-5 py-6 text-center text-muted-foreground text-sm">No check-ins yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {currentMember && members && members.length > 0 && (
        <CoachingScheduleView
          squadId={squadId}
          members={members}
          readOnly={!canManageSquad}
          currentMemberId={currentMember.id}
        />
      )}

      {showAddMember && <AddMemberModal squadId={squadId} onClose={() => setShowAddMember(false)} />}
      {showTransferCaptain && (
        <TransferCaptainModal
          squadId={squadId}
          members={members ?? []}
          currentMemberId={currentMember?.id}
          onClose={() => setShowTransferCaptain(false)}
        />
      )}
      {showSquadSettings && squad && (
        <SquadSettingsModal
          squadId={squadId}
          squad={squad}
          onClose={() => setShowSquadSettings(false)}
        />
      )}
    </div>
  );
}
